import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Notification from '@/shared/components/Notification';
import StatusDropdown from '@/shared/components/StatusDropdown';
import { useDispatch, useSelector } from "react-redux";
import { selectIntegration, setIntegration } from '@/store/slices/ReportsSlice'
import Loader from '@/shared/components/Loader';
import { MainCardWrapper } from '@/shared/components/CardWrapper'
import { useCurrentTool } from "@/shared/hooks/useCurrentTool"
import { useEnsureUser } from "@/shared/hooks/useEnsureUser"
import { useTranslation } from "react-i18next";
import ReportsSearchAndFilter from '@/features/reports/ReportsSearchAndFilter';
import ReportsListContent from '@/features/reports/ReportsListContent';
import ReportsDetailDrawer from '@/features/reports/ReportsDetailDrawer';
import { ReportsProvider } from '@/features/reports/ReportsContext';
import AddReportDialog from '@/features/reports/AddReportDialog';
import DraftsBanner from '@/features/reports/DraftsBanner';
import { useDrafts } from "@/shared/hooks/useDrafts"
import { getDraft } from "@/shared/lib/draftService"
import { useReportsQueries } from '@/features/reports/useReportsQueries'
import { useUpdateReportStatusMutation } from '@/api/rtk/reportsApi'
import { patchReportsListCaches } from '@/api/rtk/patchReportsListCaches'

function Reports() {
  const selectedTabRef = useRef(localStorage.getItem("status") || "all");
  const [page, setPage] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const [reportsQueryArgs, setReportsQueryArgs] = useState(null);
  const { reports, loading, totalCount } = useReportsQueries(reportsQueryArgs);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const { generation_name, gen_name_plural, toolInputs, toolId, toolName, currentToolFields } = useCurrentTool();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const { userId } = useEnsureUser({ forceFetch: true });
  const { drafts, deleteDraft, refreshDrafts } = useDrafts(toolId);
  const [activeDraft, setActiveDraft] = useState(null);
  const recordingSessionRef = useRef({ isActive: false, abandon: null });
  const [updateReportStatus] = useUpdateReportStatusMutation();

  const { t } = useTranslation();

  const [tabs, setTabs] = useState([]);

  useEffect(() => {
    setTabs([
      {
        name: `${t('reports.tabs.all')} ${gen_name_plural}`,
        active: true,
        value: "all",
      },
      {
        name: t('reports.tabs.processing'),
        active: false,
        value: "processing",
      },
      {
        name: t('reports.tabs.generated'),
        active: false,
        value: "generated",
      },
      {
        name: t('reports.tabs.inProgress'),
        active: false,
        value: "in-progress",
      },
      {
        name: t('reports.tabs.completed'),
        active: false,
        value: "completed",
      },
      {
        name: t('reports.tabs.urgent'),
        active: false,
        value: "urgent",
      },
    ]);
  }, [gen_name_plural, t])

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const integration = useSelector(selectIntegration);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const client = params.get("client");
    const patientId = params.get("patientId");
    const encounterId = params.get("encounterId");
    const patientIdSystem = params.get("patientIdSystem");
    const firstName = params.get("firstName") || "";
    const lastName = params.get("lastName") || "";
    const fullName = `${firstName} ${lastName}`.trim();
    const template = params.get("template");

    if (client && (patientId || encounterId) && toolInputs?.length) {
      setIsAddOpen(true);
      const id = patientId || encounterId;
      dispatch(setIntegration({
        exists: true,
        client,
        patientId: id,
        patientIdSystem,
        template: template || null,
        fullName,
        path: location.pathname,
      }));
      return;
    }

    // Client-side navigation away from integration deep-link params clears state
    // (including a locked custom template) without a full reload.
    if (integration.exists) {
      dispatch(setIntegration('reset'));
    }
  }, [location.search, toolInputs, location.pathname, dispatch, integration.exists]);

  useEffect(() => {
    if (!userId) {
      navigate("/");
    }
  }, [userId, navigate]);

  useEffect(() => {
    document.body.style.overflow = isDrawerOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isDrawerOpen]);

  const handleTabChange = async (tabType) => {
    let updatedTabs = [];
    tabs?.forEach((tab) => {
      if (tab.name === tabType) {
        localStorage.setItem("status", tab.value);
        selectedTabRef.current = tab.value;
        updatedTabs.push({
          name: tab.name,
          active: true,
          value: tab.value,
        });
      } else {
        updatedTabs.push({
          name: tab.name,
          active: false,
          value: tab.value,
        });
      }
    });
    setTabs(updatedTabs);
    setPage(1);
  };

  const handleRetry = async ({ id }) => {
    const result = await updateReportStatus({ id, status: "retry" }).unwrap().catch(() => null);
    if (result) {
      dispatch(patchReportsListCaches(
        { id, status: result?.status || 'processing' },
        { toolName, currentToolFields }
      ));
    }
  };

  const handleDraftRetry = async (draft) => {
    try {
      const fresh = await getDraft(draft.id);
      setActiveDraft(fresh ?? draft);
    } catch {
      setActiveDraft(draft);
    }
    setIsAddOpen(true);
  };

  const handleDraftDiscard = async (id) => {
    await deleteDraft(id);
  };

  const contextValue = {
    selectedTabRef,
    page,
    setPage,
    isAddOpen,
    setIsAddOpen,
    isDrawerOpen,
    setDrawerOpen,
    setShowNotification,
    handleRetry,
    activeDraft,
    setActiveDraft,
    refreshDrafts,
    recordingSessionRef,
    setReportsQueryArgs,
    reports,
    loading,
    totalCount,
  };

  return (
    <ReportsProvider value={contextValue}>
      <div className="mb-5">
        <DraftsBanner drafts={drafts} onRetry={handleDraftRetry} onDiscard={handleDraftDiscard} />
        <div className="flex justify-end items-center md:hidden mt-3">
          <AddReportDialog variant="mobile" />
        </div>

        <MainCardWrapper className='p-6 sm:pb-3'>
          <div className="flex justify-between items-center pb-[24px]">
            <div className="min-w-[132px]">
              <StatusDropdown
                tabs={tabs}
                selected={selectedTabRef.current}
                handler={handleTabChange}
                reports={true}
                showReportCount={true}
                reportsCount={totalCount}
              />
            </div>
            <ReportsSearchAndFilter />
          </div>

          <div className={`sm:min-w-full flex items-center ${(loading[selectedTabRef.current] || !generation_name) ? 'justify-center' : ''
            }`}>
            {(loading[selectedTabRef.current] || !generation_name) ? (
              <div className="w-full h-full min-h-[30vh] flex justify-center items-center">
                <Loader />
              </div>
            ) : (
              <div className="w-full h-full items-center flex flex-col self-start">
                <ReportsListContent />
              </div>
            )}
          </div>

          {showNotification && (
            <Notification
              text={"This report is being generated"}
              color={"bg-black"}
            />
          )}
        </MainCardWrapper>

        <ReportsDetailDrawer
          isDrawerOpen={isDrawerOpen}
          setDrawerOpen={setDrawerOpen}
        />
      </div>
    </ReportsProvider>
  );
}

export default Reports;
